import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

/**
 * Palette inspired by the Prizm logo — a professional-first prismatic
 * spectrum. Each variant defines the icon chip colour, a soft background
 * gradient, and a border accent so cards stay legible on both themes.
 */
const VARIANTS = {
  indigo:  { chip: "bg-indigo-500  text-white", grad: "from-indigo-500/12  via-indigo-500/5  to-transparent", ring: "ring-indigo-500/25",  bar: "bg-indigo-500"  },
  blue:    { chip: "bg-sky-500     text-white", grad: "from-sky-500/12     via-sky-500/5     to-transparent", ring: "ring-sky-500/25",     bar: "bg-sky-500"     },
  cyan:    { chip: "bg-cyan-500    text-white", grad: "from-cyan-500/12    via-cyan-500/5    to-transparent", ring: "ring-cyan-500/25",    bar: "bg-cyan-500"    },
  emerald: { chip: "bg-emerald-500 text-white", grad: "from-emerald-500/12 via-emerald-500/5 to-transparent", ring: "ring-emerald-500/25", bar: "bg-emerald-500" },
  lime:    { chip: "bg-lime-500    text-white", grad: "from-lime-500/12    via-lime-500/5    to-transparent", ring: "ring-lime-500/25",    bar: "bg-lime-500"    },
  amber:   { chip: "bg-amber-500   text-white", grad: "from-amber-500/12   via-amber-500/5   to-transparent", ring: "ring-amber-500/25",   bar: "bg-amber-500"   },
  orange:  { chip: "bg-orange-500  text-white", grad: "from-orange-500/12  via-orange-500/5  to-transparent", ring: "ring-orange-500/25",  bar: "bg-orange-500"  },
  rose:    { chip: "bg-rose-500    text-white", grad: "from-rose-500/12    via-rose-500/5    to-transparent", ring: "ring-rose-500/25",    bar: "bg-rose-500"    },
  pink:    { chip: "bg-pink-500    text-white", grad: "from-pink-500/12    via-pink-500/5    to-transparent", ring: "ring-pink-500/25",    bar: "bg-pink-500"    },
  violet:  { chip: "bg-violet-500  text-white", grad: "from-violet-500/12  via-violet-500/5  to-transparent", ring: "ring-violet-500/25",  bar: "bg-violet-500"  },
  // Backwards-compat aliases used by the old `tone` prop
  default: { chip: "bg-primary/15 text-primary", grad: "from-primary/8 via-primary/3 to-transparent", ring: "ring-border/60", bar: "bg-primary" },
  primary: { chip: "bg-primary text-primary-foreground", grad: "from-primary/12 via-primary/5 to-transparent", ring: "ring-primary/25", bar: "bg-primary" },
  success: { chip: "bg-emerald-500 text-white", grad: "from-emerald-500/12 via-emerald-500/5 to-transparent", ring: "ring-emerald-500/25", bar: "bg-emerald-500" },
  warning: { chip: "bg-amber-500 text-white", grad: "from-amber-500/12 via-amber-500/5 to-transparent", ring: "ring-amber-500/25", bar: "bg-amber-500" },
  danger:  { chip: "bg-red-500 text-white", grad: "from-red-500/12 via-red-500/5 to-transparent", ring: "ring-red-500/25", bar: "bg-red-500" },
};

export function KpiCard({ label, value, delta, deltaLabel, suffix = "", tone, variant, icon: Icon, testid, index = 0 }) {
  const positive = typeof delta === "number" ? delta >= 0 : true;
  const v = VARIANTS[variant] || VARIANTS[tone] || VARIANTS.default;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl p-5 card-lift ring-1 ${v.ring} bg-gradient-to-br ${v.grad} bg-card`}
    >
      {/* Left accent bar */}
      <span className={`absolute left-0 top-4 bottom-4 w-1 rounded-r-full ${v.bar} opacity-80`} aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <div className="label-caps">{label}</div>
        {Icon ? (
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center shadow-sm ${v.chip}`}>
            <Icon className="w-4 h-4" strokeWidth={2.25} />
          </div>
        ) : null}
      </div>
      <div className="mt-3 flex items-end gap-1.5">
        <div className="number text-4xl font-medium tracking-tight">
          {value}
          {suffix && <span className="text-lg text-muted-foreground font-light">{suffix}</span>}
        </div>
      </div>
      {typeof delta === "number" && (
        <div className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${positive ? "text-emerald-500" : "text-red-500"}`}>
          {positive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
          {Math.abs(delta)}% {deltaLabel && <span className="text-muted-foreground font-normal">{deltaLabel}</span>}
        </div>
      )}
    </motion.div>
  );
}

export function SectionCard({ title, subtitle, actions, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`data-card p-5 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">{title}</h3>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-display font-semibold text-3xl md:text-4xl tracking-tighter">{title}</h1>
        {subtitle && <div className="text-muted-foreground mt-1 text-sm md:text-base">{subtitle}</div>}
      </div>
      {actions}
    </div>
  );
}
